/*! Hyspecs — Free Gift with Purchase, auto-add/remove (generic, N offers).
 *
 * This script puts a gift line in the cart once the customer qualifies, and
 * takes it back out below threshold. Two delivery modes (the snippet resolves
 * which variant id to add; this script's add/remove logic is identical for
 * both):
 *   - "discount": adds the REAL product; a Shopify discount Function zeroes
 *     it (Functions can't insert lines, only discount existing ones). Loses
 *     best-value arbitration when the cart also carries an order-discount code.
 *   - "free_variant": adds a hidden $0 gift variant — free by nature, so it's
 *     never a discount and never competes with codes. Preferred. See
 *     bundle-fn-scaffold Free-Gift-Decoupled-Spec.
 *
 * Threshold basis is per gift (min_spend_basis):
 *   - "original" (default): original_line_price, PRE-discount, so an
 *     automatic discount or a checkout code can't drop the cart under
 *     threshold and yank the gift.
 *   - "final": final_line_price minus cart-level discounts, i.e. what the
 *     cart shows after automatic discounts (the Knox "$99 after discounts"
 *     rule). Codes typed at checkout are invisible here either way.
 * Both are merchandise-value rules: lines this script added, system owned $0
 * gift variants and gift cards never count; anything the customer paid for
 * does, including a full-price purchase of a legacy gift product.
 *
 * A gift is live only while its entry is "active" (stock gate + country,
 * resolved in Liquid) AND now is inside starts_at/ends_at (checked here, so a
 * cached page can't extend the offer). An owned $0 variant whose gift is not
 * live is stripped from the cart, tagged or not.
 *
 * Config is data-driven, not hardcoded: it comes from the "hys-gift-config"
 * JSON script tag (see snippets/hys-gift-auto-add.liquid), which merges
 * shop.metafields.bundle.gifts (written by the hosted admin app on every
 * bundle save, app/lib/bundles.server.ts) with the theme-owned
 * shop.metafields.hys.promo_gifts. Add, edit, or remove a gift offer there
 * and this script picks it up on the next page load with no code change.
 *
 * Never removes a gift line the customer added themselves (no matching
 * property) — only ever touches lines this script itself tagged, and only
 * with the bundle_id that added them, so two different gift offers can
 * never remove each other's line. Exceptions: owned $0 variants (never
 * legitimately purchasable) and tagged lines whose offer is gone (orphans
 * from an ended offer, e.g. saved carts; stacking review F3/C5).
 */
import Cart from "cart-store";

const CONFIG_ELEMENT_ID = "hys-gift-config";
const PROPERTY_KEY = "_hys_auto_gift";

let syncing = false;
// Newest cart seen while a pass was running. Cart events that land during an
// await used to be dropped; now the latest one is replayed once the pass ends.
let pendingCart = null;

/** Parsed config array, or null when the tag is missing or unparseable. Null
 *  means "don't touch the cart at all": a broken config must never read as
 *  "no offers" and strip every gift line. */
function readGiftConfigs() {
  const el = document.getElementById(CONFIG_ELEMENT_ID);
  if (!el) return null;
  try {
    const parsed = JSON.parse(el.textContent || "[]");
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function thresholdCents(gift, currency) {
  const major = gift.min_spend_by_currency?.[currency] ?? gift.min_spend;
  const cents = Math.round(Number(major) * 100);
  return Number.isFinite(cents) && cents > 0 ? cents : null;
}

/** Inside the offer window? Missing or unparseable bounds don't restrict. */
function inWindow(gift, now = Date.now()) {
  const starts = Date.parse(gift.starts_at ?? "");
  const ends = Date.parse(gift.ends_at ?? "");
  if (Number.isFinite(starts) && now < starts) return false;
  if (Number.isFinite(ends) && now > ends) return false;
  return true;
}

function isLive(gift) {
  return gift.active !== false && inWindow(gift);
}

// cart.js returns line-item properties as strings while the config emits
// bundle_id via `| json` (often a number), so compare as strings.
function sameBundle(value, bundleId) {
  return String(value ?? "") === String(bundleId ?? "");
}

function isAutoGiftLine(item) {
  return item?.properties?.[PROPERTY_KEY] != null && item.properties[PROPERTY_KEY] !== "";
}

/** Merchandise value that counts toward EVERY gift's threshold, in both
 *  bases. Excludes, for all gifts at once (so one gift can never unlock
 *  another):
 *    - lines this script added (tagged _hys_auto_gift),
 *    - any line of a system-owned $0 gift variant (never purchasable),
 *    - gift cards: their value re-enters a future cart at redemption, where
 *      the gift can be earned again, so one real dollar must not qualify
 *      twice (cart.js items carry a native gift_card boolean).
 *  "final" also takes off cart-level (order) discounts applied in the cart. */
function eligibleSpendCents(cart, ownedVariantIds) {
  const eligible = cart.items.filter(
    (item) => !isAutoGiftLine(item) && !ownedVariantIds.has(item.variant_id) && !item.gift_card,
  );
  const original = eligible.reduce((sum, item) => sum + (item.original_line_price || 0), 0);
  const lineFinal = eligible.reduce((sum, item) => sum + (item.final_line_price ?? item.original_line_price ?? 0), 0);
  const cartLevel = (cart.cart_level_discount_applications || []).reduce(
    (sum, app) => sum + (Number(app.total_allocated_amount) || 0),
    0,
  );
  return { original, final: Math.max(0, lineFinal - cartLevel) };
}

/** One action per call (add/remove), then return — the resulting
 *  on:cart:loaded re-invokes this for the next gift, if any. Avoids
 *  racing multiple /cart mutations against a single stale cart snapshot. */
async function syncGifts(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  if (syncing) {
    pendingCart = cart;
    return;
  }
  const gifts = readGiftConfigs();
  if (gifts === null) return;
  // Nothing configured and nothing tagged: no work, no cart calls.
  if (!gifts.length && !cart.items.some(isAutoGiftLine)) return;

  syncing = true;
  try {
    await syncOnce(cart, gifts);
  } catch (error) {
    // A failed /cart mutation must not strand a queued cart event below.
    console.warn("[hys-gift-auto-add] sync failed", error);
  } finally {
    syncing = false;
  }

  // Replay the newest cart that arrived mid-pass, if any. Cleared before the
  // call so a replay that itself mutates can queue the next one.
  if (pendingCart) {
    const next = pendingCart;
    pendingCart = null;
    syncGifts(next);
  }
}

async function syncOnce(cart, gifts) {
  const ownedVariantIds = new Set(gifts.filter((g) => g.owned && g.variant_id).map((g) => g.variant_id));
  const spend = eligibleSpendCents(cart, ownedVariantIds);

  // Orphans first: a tagged line whose offer is no longer configured (offer
  // ended or was removed, cart saved from an old promo). Lines of an owned
  // variant are left to the per-gift pass below, which owns those rules.
  const orphan = cart.items.find(
    (item) =>
      isAutoGiftLine(item) &&
      !ownedVariantIds.has(item.variant_id) &&
      !gifts.some((g) => g.variant_id === item.variant_id && sameBundle(item.properties[PROPERTY_KEY], g.bundle_id)),
  );
  if (orphan) {
    await Cart.change({ id: orphan.key, quantity: 0 });
    return;
  }

  for (const gift of gifts) {
    if (!gift.variant_id) continue;
    const variantLines = cart.items.filter((item) => item.variant_id === gift.variant_id);
    const threshold = thresholdCents(gift, cart.currency);
    const live = isLive(gift) && threshold !== null;
    const qualifies = live && (gift.min_spend_basis === "final" ? spend.final : spend.original) >= threshold;

    if (gift.owned) {
      // System-owned $0 gift variant (free_variant delivery): it is never
      // legitimately purchasable, so enforce the rules on ANY line of it,
      // tagged or not, including ones added via search or a direct URL
      // (abuse found 2026-07-04: add below threshold, or qty > 1).
      if (!variantLines.length) {
        if (qualifies) {
          await addGift(gift);
          return; // triggers another on:cart:loaded; next pass handles the rest
        }
        continue;
      }
      if (!qualifies) {
        await Cart.change({ id: variantLines[0].key, quantity: 0 });
        return;
      }
      if (variantLines.length > 1) {
        // Keep one line, preferring the one this script tagged.
        const extra = variantLines.find((item) => !sameBundle(item.properties?.[PROPERTY_KEY], gift.bundle_id)) ?? variantLines[1];
        await Cart.change({ id: extra.key, quantity: 0 });
        return;
      }
      if ((Number(variantLines[0].quantity) || 1) > 1) {
        await Cart.change({ id: variantLines[0].key, quantity: 1 });
        return;
      }
      continue;
    }

    // Legacy discount delivery adds the REAL sellable product. Only the line
    // this script tagged with THIS bundle_id is the gift; an untagged line of
    // the same variant is something the customer bought and is never touched.
    const autoLine = variantLines.find((item) => sameBundle(item.properties?.[PROPERTY_KEY], gift.bundle_id));

    if (!autoLine && qualifies) {
      await addGift(gift);
      return;
    }
    if (autoLine && !qualifies) {
      await Cart.change({ id: autoLine.key, quantity: 0 });
      return;
    }
  }
}

function addGift(gift) {
  return Cart.add({
    id: gift.variant_id,
    quantity: 1,
    [`properties[${PROPERTY_KEY}]`]: String(gift.bundle_id),
  });
}

/** Flags gift lines in the cart UI (drawer and /cart) with data-hys-gift, so
 *  CSS can say "Free" instead of $0.00 and drop the stepper and remove link
 *  (assets/rdc.css, "Free gift lines"): the watcher would only put a removed
 *  gift straight back, and holds it at one per order. Cart items re-render on
 *  every change, so this re-runs on cart events and DOM changes. */
let lastCart = null;
function markGiftLines(cart = lastCart) {
  if (!cart || !Array.isArray(cart.items)) return;
  lastCart = cart;
  const owned = new Set((readGiftConfigs() || []).filter((g) => g.owned).map((g) => g.variant_id));
  const giftKeys = new Set(cart.items.filter((item) => isAutoGiftLine(item) || owned.has(item.variant_id)).map((item) => item.key));
  document.querySelectorAll("cart-item[key]").forEach((el) => {
    el.toggleAttribute("data-hys-gift", giftKeys.has(el.getAttribute("key")));
  });
}

document.addEventListener("on:cart:loaded", (event) => {
  syncGifts(event.detail?.cart);
  markGiftLines(event.detail?.cart);
});

if (typeof MutationObserver !== "undefined") {
  const observer = new MutationObserver(() => markGiftLines());
  document.querySelectorAll("interactive-cart").forEach((el) => observer.observe(el, { childList: true, subtree: true }));
}

const initialCart = Cart.getState().cart;
if (initialCart && Array.isArray(initialCart.items)) {
  syncGifts(initialCart);
  markGiftLines(initialCart);
}
